import React, { useState, useEffect, useCallback } from "react";
import {
  IconButton,
  CircularProgress,
  Alert,
  Avatar,
  Snackbar,
  Dialog,
  DialogContent,
  Tooltip,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import PropTypes from "prop-types";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import DataTable from "shared/components/Tables/DataTable";
import CustomModal from "shared/components/MDmodal/CustomModal";
import { getAllDevice } from "modules/bitacora/services/Configuracion/Equipo/equipoService";
import { updateDeviceService } from "modules/bitacora/services/Configuracion/Equipo/equipoService";
import { deleteDeviceService } from "modules/bitacora/services/Configuracion/Equipo/equipoService";
import FormDevice from "../Form/FormDevice";

function TablaDevice({ refreshFlag }) {
  const [devices, setDevices] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [deviceSelect, setDeviceSelect] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);

  const showNotification = useCallback((message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const fetchDevice = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllDevice();
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;
      setDevices(parsedData || []);
    } catch (err) {
      console.error("❌ Error al obtener equipos:", err);
      showNotification("Error al cargar equipos", "error");
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchDevice();
  }, [refreshFlag]);

  const handleEditar = useCallback((device) => {
    setDeviceSelect(device);
    setOpenModal(true);
  }, []);

  const handleEliminarClick = useCallback((id) => {
    setDeviceToDelete(id);
    setOpenDeleteDialog(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deviceToDelete) return;
    try {
      setLoading(true);
      await deleteDeviceService(deviceToDelete);
      await fetchDevice();
      showNotification("Equipo eliminado correctamente", "success");
    } catch (err) {
      console.error("Error al eliminar el equipo:", err);
      const errorMessage = err.message?.includes("token")
        ? "Su sesión ha expirado. Por favor inicie sesión nuevamente."
        : "Error al eliminar equipo";
      showNotification(errorMessage, "error");
    } finally {
      setLoading(false);
      setOpenDeleteDialog(false);
      setDeviceToDelete(null);
    }
  }, [deviceToDelete, fetchDevice, showNotification]);

  const handleActualizar = useCallback(
    async (datos) => {
      if (!deviceSelect) return;
      try {
        setLoading(true);
        await updateDeviceService(deviceSelect.id, {
          commerceId: datos.commerceId.name,
          zoneId: datos.zoneId.name,
          nomenclature: datos.nomenclature,
          description: datos.description,
          status: datos.status,
        });
        await fetchDevice();
        setOpenModal(false);
        showNotification("Equipo actualizado correctamente", "success");
      } catch (err) {
        console.error("Error al actualizar equipo:", err);
        const message = err.response?.data?.message || "Error al actualizar equipo";
        showNotification(message, "error");
      } finally {
        setLoading(false);
      }
    },
    [deviceSelect, fetchDevice, showNotification]
  );

  const columns = [
    { Header: "Nombre", accessor: "nomenclature", width: "30%" },
    { Header: "Comercio", accessor: "commerceId", width: "40%" },
    { Header: "Zona", accessor: "zoneId", width: "40%" },
    { Header: "Descripción", accessor: "description", width: "30%" },
    { Header: "Estado", accessor: "status", width: "40%" },
    { Header: "Acciones", accessor: "acciones", align: "center", width: "10%" },
  ];
  const rows = devices.map((device) => ({
    nomenclature: (
      <MDTypography variant="button" fontWeight="medium">
        {device.nomenclature || "Sin nombre"}
      </MDTypography>
    ),
    commerceId: (
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {device.commerce.name || "Sin comercio"}
      </MDTypography>
    ),
    zoneId: (
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {device.zone.title || "Sin zona"}
      </MDTypography>
    ),
    description: (
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {device.description || "Sin descripción"}
      </MDTypography>
    ),
    status: (
      <MDTypography
        variant="caption"
        color={device.status === true ? "success" : "error"}
        fontWeight="medium"
      >
        {device.status === true ? "Activo" : "Inactivo"}
      </MDTypography>
    ),
    acciones: (
      <MDBox display="flex" justifyContent="center">
        <Tooltip title="Editar Equipo">
          <IconButton color="info" onClick={() => handleEditar(device)}>
            <Edit />
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar Equipo">
          <IconButton color="error" onClick={() => handleEliminarClick(device.id)}>
            <Delete />
          </IconButton>
        </Tooltip>
      </MDBox>
    ),
  }));

  return (
    <>
      <MDBox
        mx={-1}
        mt={-1}
        py={2}
        px={2}
        variant="gradient"
        bgColor="success"
        borderRadius="lg"
        coloredShadow="info"
      >
        <MDTypography variant="h6" color="white">
          Gestión de Equipos
        </MDTypography>
      </MDBox>

      <MDBox pt={1} px={-2}>
        {loading ? (
          <MDBox display="flex" justifyContent="center" p={3}>
            <CircularProgress color="info" />
          </MDBox>
        ) : error ? (
          <MDBox p={2}>
            <Alert severity="error">{error}</Alert>
          </MDBox>
        ) : (
          <DataTable
            table={{ columns, rows }}
            isSorted
            entriesPerPage={false}
            showTotalEntries
            noEndBorder
          />
        )}
      </MDBox>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={openDeleteDialog}
        onClose={() => !loading && setOpenDeleteDialog(false)}
        PaperProps={{
          sx: { minWidth: "400px", borderRadius: "12px", padding: "16px" },
        }}
      >
        <MDBox display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: "error.light", mr: 2 }}>
            <Delete color="error" />
          </Avatar>
          <MDTypography variant="h6" fontWeight="medium">
            Confirmar eliminación
          </MDTypography>
        </MDBox>

        <DialogContent>
          <MDTypography variant="body2">Esta acción eliminará el equipo del sistema.</MDTypography>
          <MDTypography variant="body2" fontWeight="bold" mt={1}>
            ¿Deseas continuar?
          </MDTypography>
        </DialogContent>

        <MDBox display="flex" justifyContent="space-between" px={3} pb={2}>
          <MDButton
            onClick={() => setOpenDeleteDialog(false)}
            variant="outlined"
            color="error"
            disabled={loading}
            sx={{ minWidth: "120px" }}
          >
            Cancelar
          </MDButton>
          <MDButton
            onClick={confirmDelete}
            color="error"
            variant="gradient"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ minWidth: "120px" }}
          >
            {loading ? "Eliminando..." : "Confirmar"}
          </MDButton>
        </MDBox>
      </Dialog>

      <CustomModal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setError(null);
        }}
        title={`Editar equipo - ${deviceSelect?.nomenclature || ""}`}
      >
        {deviceSelect && (
          <FormDevice
            initialData={{
              nomenclature: deviceSelect?.nomenclature || "",
              description: deviceSelect?.description || "",
              commerceId: deviceSelect?.commerceId ?? "",
              zoneId: deviceSelect?.zoneId ?? "",
              status: deviceSelect?.status || true,
            }}
            onSubmit={handleActualizar}
            isEditMode
            loading={loading}
          />
        )}
      </CustomModal>
    </>
  );
}

TablaDevice.propTypes = {
  refreshFlag: PropTypes.bool,
};

export default TablaDevice;
